import React from 'react';
import { MenuItem, ListSubheader } from '@mui/material';
import { SelectOptionWithCategory } from '../model';
import {
  BaseSelectInputProps,
  useSelectInputStyles,
  createSelectInputChangeHandler,
  SelectInputWrapper,
} from './SelectInputShared';

interface SelectInputWithCategoriesProps extends BaseSelectInputProps {
  choices: SelectOptionWithCategory[];
  defaultChoice?: { label: string; value: string };
}

const SelectInputWithCategories: React.FC<SelectInputWithCategoriesProps> = (props) => {
  const {
    label,
    onChange,
    valueChanged,
    styleOverrides,
    errorMessage,
    value = '',
    choices,
    defaultChoice,
    disabled = false,
  } = props;

  const classes = useSelectInputStyles();
  const handleChange = createSelectInputChangeHandler(onChange, valueChanged);

  const createMenuItem = (choice: { label: string; value: string }, indented: boolean = false) =>
    choice ? (
      <MenuItem value={choice.value} key={choice.value} sx={indented ? { pl: 4 } : {}}>
        <span>{choice.label}</span>
      </MenuItem>
    ) : null;

  const defaultMenuItem = createMenuItem(defaultChoice);

  // Group options by category
  const categories = Array.from(new Set(choices.map((choice) => choice.category)));

  const menuItemChoices = categories.flatMap((category) => {
    const categoryOptions = choices.filter((choice) => choice.category === category);
    return [
      <ListSubheader
        key={`header-${category}`}
        disableSticky
        sx={{
          fontWeight: 'bold',
          fontSize: '0.875rem',
          lineHeight: 1.75,
          color: 'text.primary',
          pt: 1,
          pb: 0.5,
        }}
      >
        {category}
      </ListSubheader>,
      ...categoryOptions.map((choice) => createMenuItem(choice, true)),
    ];
  });

  return (
    <SelectInputWrapper
      label={label}
      value={value}
      errorMessage={errorMessage}
      styleOverrides={styleOverrides}
      disabled={disabled}
      classes={classes}
      onChange={handleChange}
    >
      {defaultMenuItem}
      {menuItemChoices}
    </SelectInputWrapper>
  );
};

export default SelectInputWithCategories;
